// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrencySymbol } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import { FaFilter, FaTimes, FaChevronDown, FaSortAmountUp, FaSortAmountDown, FaShareAlt, FaWhatsapp, FaCommentAlt, FaComments, FaCopy } from 'react-icons/fa';
import ProfileRecipientPopup from './ProfileRecipientPopup';
import { addChatMessage } from '@services/firebaseChat';
import { getFirebase } from '@services/firebase';

const HomeFilter = ({ profiles, userRole, filters, onChange, filteredCount, priceSort, onPriceSortChange, globalMin: gMinProp, globalMax: gMaxProp, searchQuery }) => {
  const [open, setOpen] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [showRecipientPopup, setShowRecipientPopup] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareLinks, setShareLinks] = useState({ whatsapp: '', sms: '' });
  const [pendingShareUrl, setPendingShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [linkDuration, setLinkDuration] = useState(30); // minutes
  const panelRef = useRef(null);
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const navigate = useNavigate();

  const showPriceFilter = userRole !== 'User' && userRole !== 'Mover';

  // Derive option lists from all profiles
  const allShapes = [...new Set(
    profiles.flatMap(p => Array.isArray(p.cshape) ? p.cshape : []).filter(Boolean)
  )].sort();

  const allEvents = [...new Set(
    profiles.flatMap(p => Array.isArray(p.cevent) ? p.cevent : []).filter(Boolean)
  )].sort();

  const allFlavours = [...new Set(
    profiles.map(p => p.cflavour).filter(Boolean)
  )].sort();

  const allThemes = [...new Set(
    profiles.map(p => p.theme).filter(t => t && t.trim() !== '')
  )].sort();

  // Use pre-computed bounds from Home.jsx; fall back to deriving locally if not yet available
  const { globalMin, globalMax } = (() => {
    if (gMinProp !== undefined && gMaxProp !== undefined && gMaxProp > gMinProp)
      return { globalMin: gMinProp, globalMax: gMaxProp };
    const prices = profiles
      .map(p => parseFloat(String(p.ccost || '').replace(/[^0-9.]/g, '')))
      .filter(n => !isNaN(n) && n > 0);
    if (!prices.length) return { globalMin: 0, globalMax: 0 };
    return { globalMin: Math.floor(Math.min(...prices)), globalMax: Math.ceil(Math.max(...prices)) };
  })();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeCount = [
    showPriceFilter && (filters.priceRange[0] > globalMin || filters.priceRange[1] < globalMax),
    filters.shapes.length > 0,
    filters.events.length > 0,
    filters.flavours.length > 0,
    filters.themes.length > 0,
  ].filter(Boolean).length;

  const handleToggleItem = (field, value) => {
    const current = filters[field];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    onChange({ ...filters, [field]: next });
  };

  const handlePriceMin = (val) => {
    const num = Math.max(globalMin, Math.min(Number(val), filters.priceRange[1]));
    onChange({ ...filters, priceRange: [num, filters.priceRange[1]] });
  };

  const handlePriceMax = (val) => {
    const num = Math.min(globalMax, Math.max(Number(val), filters.priceRange[0]));
    onChange({ ...filters, priceRange: [filters.priceRange[0], num] });
  };

  const handleClear = () => {
    onChange({
      priceRange: [globalMin, globalMax],
      shapes: [],
      events: [],
      flavours: [],
      themes: [],
      mostLiked: false,
      inOffer: false,
    });
  };

  const isAdminOrSuper = userRole === 'Admin' || userRole === 'Super';

  const buildShareUrl = () => {
    // Compact payload — only store non-default values with short keys
    const p = { e: Date.now() + linkDuration * 60 * 1000 };
    if (filters.priceRange[0] !== globalMin || filters.priceRange[1] !== globalMax)
      p.pr = filters.priceRange;
    if (filters.shapes.length)   p.s  = filters.shapes;
    if (filters.events.length)   p.ev = filters.events;
    if (filters.flavours.length) p.fl = filters.flavours;
    if (filters.themes.length)   p.th = filters.themes;
    if (priceSort !== 'asc')     p.ps = priceSort;
    if (searchQuery)             p.sq = searchQuery;
    const token = btoa(JSON.stringify(p));
    return `${window.location.origin}/shared?t=${token}`;
  };

  const openShareModal = () => {
    const url = buildShareUrl();
    setShareUrl(url);
    setPendingShareUrl(url);
    setShareLinks({
      whatsapp: 'https://wa.me/?text=' + encodeURIComponent('Check out these cakes! ' + url),
      sms: 'sms:?body=' + encodeURIComponent('Check out these cakes! ' + url),
    });
    setShareModal(true);
    setOpen(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRecipientSelect = async (recipientUser) => {
    const { auth } = getFirebase();
    const currentUser = auth.currentUser;
    if (!currentUser || !pendingShareUrl) return;
    try {
      await addChatMessage(currentUser.uid, {
        text: '🔗 Shared view: ' + pendingShareUrl,
        senderId: currentUser.uid,
        senderRole: userRole,
        receiverId: recipientUser.id,
        type: 'text',
      });
      setShowRecipientPopup(false);
      setShareModal(false);
      navigate('/admin-chat');
    } catch (err) {
      console.error('Failed to send share link:', err);
    }
  };

  const priceRangeActive = showPriceFilter && (filters.priceRange[0] > globalMin || filters.priceRange[1] < globalMax);

  return (
    <React.Fragment>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {/* Price sort icon — shown when price range filter is active */}
      {priceRangeActive && (
        <button
          onClick={() => onPriceSortChange(priceSort === 'asc' ? 'desc' : 'asc')}
          title={priceSort === 'asc' ? 'Price: Low to High' : 'Price: High to Low'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#fa4848', color: '#fff',
            border: 'none', borderRadius: '50%',
            width: '40px', height: '40px', minWidth: '40px', minHeight: '40px',
            cursor: 'pointer', flexShrink: 0,
            boxShadow: '0 1px 5px rgba(0,0,0,0.18)', padding: 0,
          }}
        >
          {priceSort === 'asc' ? <FaSortAmountUp size={13} /> : <FaSortAmountDown size={13} />}
        </button>
      )}
      <div ref={panelRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger button — icon only */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
          background: activeCount > 0 ? '#fa4848' : (isDark ? '#2b2b2b' : '#fff'),
          color: activeCount > 0 ? '#fff' : '#fa4848',
          border: 'none', borderRadius: '50%',
          width: '40px', height: '40px', minWidth: '40px', minHeight: '40px',
          cursor: 'pointer', flexShrink: 0,
          boxShadow: isDark ? '0 1px 5px rgba(0,0,0,0.4)' : '0 1px 5px rgba(0,0,0,0.18)',
          padding: 0,
        }}
      >
        <FaFilter size={13} />
        {activeCount > 0 && (
          <span style={{
            background: '#fff', color: '#fa4848', borderRadius: '50%',
            width: '17px', height: '17px', fontSize: '10px', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{activeCount}</span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: isDark ? '#2b2b2b' : '#fff', borderRadius: '12px', width: '260px',
          boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.18)', zIndex: 2000,
          padding: '12px 12px 8px',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontWeight: '600', fontSize: '14px', color: isDark ? '#ececec' : '#222' }}>{activeCount > 0 ? (filteredCount + ' ' + (filteredCount === 1 ? 'match' : 'matches')) : 'Filters'}</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {activeCount > 0 && (
                <button onClick={handleClear} style={{
                  background: 'none', border: 'none', color: '#fa4848', fontSize: '12px',
                  cursor: 'pointer', fontWeight: '500', padding: 0,
                }}>Clear all</button>
              )}
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#999' : '#888', padding: 0 }}>
                <FaTimes size={14} />
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '72vh', overflowY: 'auto' }}>

            {/* Price Range */}
            {showPriceFilter && globalMax > globalMin && (
              <CollapsibleSection label="Price Range" hasActive={filters.priceRange[0] > globalMin || filters.priceRange[1] < globalMax}>
                <DualRangeSlider
                  min={globalMin} max={globalMax}
                  valueMin={filters.priceRange[0]}
                  valueMax={filters.priceRange[1]}
                  onChangeMin={v => handlePriceMin(v)}
                  onChangeMax={v => handlePriceMax(v)}
                />
              </CollapsibleSection>
            )}

            {/* Shape */}
            {allShapes.length > 0 && (
              <CollapsibleSection label="Category" hasActive={filters.shapes.length > 0}>
                <ChipGroup items={allShapes} selected={filters.shapes} onToggle={v => handleToggleItem('shapes', v)} />
              </CollapsibleSection>
            )}

            {/* Event */}
            {allEvents.length > 0 && (
              <CollapsibleSection label="Event" hasActive={filters.events.length > 0}>
                <ChipGroup items={allEvents} selected={filters.events} onToggle={v => handleToggleItem('events', v)} />
              </CollapsibleSection>
            )}

            {/* Flavour */}
            {allFlavours.length > 0 && (
              <CollapsibleSection label="Flavour" hasActive={filters.flavours.length > 0}>
                <ChipGroup items={allFlavours} selected={filters.flavours} onToggle={v => handleToggleItem('flavours', v)} />
              </CollapsibleSection>
            )}

            {/* Theme */}
            {allThemes.length > 0 && (
              <CollapsibleSection label="Theme" hasActive={filters.themes.length > 0}>
                <ChipGroup items={allThemes} selected={filters.themes} onToggle={v => handleToggleItem('themes', v)} />
              </CollapsibleSection>
            )}

            {/* Most Liked — coming soon */}
            <CollapsibleSection label="Most Liked" hasActive={false}>
              <span style={{ fontSize: '11px', color: isDark ? '#777' : '#aaa' }}>Coming soon</span>
            </CollapsibleSection>

            {/* In Offer — coming soon */}
            <CollapsibleSection label="In Offer" hasActive={false}>
              <span style={{ fontSize: '11px', color: isDark ? '#777' : '#aaa' }}>Coming soon</span>
            </CollapsibleSection>

            {/* Share button — Admin / Super only */}
            {isAdminOrSuper && (
              <div style={{ paddingTop: '10px', paddingBottom: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <select
                  value={linkDuration}
                  onChange={function(e) { setLinkDuration(Number(e.target.value)); }}
                  style={{
                    border: isDark ? '1px solid #3a3a3a' : '1px solid #e0e0e0', borderRadius: '8px',
                    padding: '7px 8px', fontSize: '12px', color: isDark ? '#ccc' : '#444',
                    background: isDark ? '#222' : '#f9f9f9', cursor: 'pointer', width: '100%',
                  }}
                >
                  {[5, 10, 15, 30, 45, 60].map(m => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
                <button onClick={openShareModal} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#fa4848', color: '#fff', border: 'none', borderRadius: '10px',
                  padding: '9px 0', width: '100%', fontSize: '16px', cursor: 'pointer',
                }}>
                  <FaShareAlt size={15} />
                </button>
              </div>
            )}

          </div>
        </div>
      )}
      </div>
    </div>

    {/* Share modal */}
    {shareModal && (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 3000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}
        onClick={() => setShareModal(false)}
      >
        <div
          style={{
            background: isDark ? '#1e1e1e' : '#fff',
            borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '440px',
            padding: '12px 20px 36px',
            boxShadow: isDark ? '0 -4px 32px rgba(0,0,0,0.6)' : '0 -4px 24px rgba(0,0,0,0.12)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: isDark ? '#444' : '#ddd' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <ShareIconBtn icon={<FaComments size={20} />} label="Inchat" color="#fa4848" onClick={() => setShowRecipientPopup(true)} />
            <ShareIconBtn icon={<FaWhatsapp size={20} />} label="WhatsApp" color="#25d366" onClick={() => window.open(shareLinks.whatsapp, '_blank')} />
            <ShareIconBtn icon={<FaCommentAlt size={20} />} label="Message" color="#4a90e2" onClick={() => window.open(shareLinks.sms)} />
            <ShareIconBtn icon={<FaCopy size={20} />} label={copied ? 'Copied!' : 'Copy'} color={copied ? '#4caf50' : '#888'} onClick={copyLink} />
          </div>
        </div>
      </div>
    )}

    <ProfileRecipientPopup
      isOpen={showRecipientPopup}
      onClose={() => setShowRecipientPopup(false)}
      onSelectRecipient={handleRecipientSelect}
      currentUser={getFirebase().auth.currentUser}
    />
    </React.Fragment>
  );
};

const ShareIconBtn = ({ icon, label, color, onClick }) => {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
      background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
    }}>
      <div style={{
        width: '46px', height: '46px', borderRadius: '14px',
        background: color + '1a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color,
      }}>{icon}</div>
      <span style={{ fontSize: '10px', color: isDark ? '#aaa' : '#666', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
};

const DualRangeSlider = ({ min, max, valueMin, valueMax, onChangeMin, onChangeMax }) => {
  const trackRef = useRef(null);
  const dragging = useRef(null); // 'min' | 'max'
  const range = max - min || 1;
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  const pct = v => ((v - min) / range) * 100;
  const valFromX = useCallback((clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(min + ratio * range);
  }, [min, range]);

  const onPointerDown = (thumb, e) => {
    e.preventDefault();
    dragging.current = thumb;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const onPointerMove = useCallback((e) => {
    if (!dragging.current || !trackRef.current) return;
    const v = valFromX(e.clientX);
    if (dragging.current === 'min') onChangeMin(Math.min(v, valueMax));
    else onChangeMax(Math.max(v, valueMin));
  }, [valFromX, valueMin, valueMax, onChangeMin, onChangeMax]);

  const onPointerUp = useCallback(() => {
    dragging.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const dotStyle = (active) => ({
    position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
    width: '24px', height: '24px', borderRadius: '50%', cursor: 'pointer',
    background: '#fa4848',
    boxShadow: active ? '0 0 0 4px rgba(250,72,72,0.18), 0 2px 6px rgba(0,0,0,0.25)' : '0 2px 6px rgba(0,0,0,0.22)',
    touchAction: 'none', zIndex: active ? 2 : 1,
    // Extend the invisible tap area so fingers don't have to be pixel-perfect
    outline: 'none',
  });

  return (
    <div style={{ padding: '4px 8px 6px 8px' }}>
      {/* Track — horizontal margins equal the thumb radius so the thumb
          circles at 0 % and 100 % never overflow the slider area */}
      <div ref={trackRef} style={{
        position: 'relative', height: '6px', background: isDark ? '#444' : '#e0e0e0',
        borderRadius: '6px', margin: '16px 12px 20px', cursor: 'default',
      }}>
        {/* Active fill */}
        <div style={{
          position: 'absolute', height: '100%', background: '#fa4848', borderRadius: '6px',
          left: `${pct(valueMin)}%`, right: `${100 - pct(valueMax)}%`,
        }} />
        {/* Min dot */}
        <div
          style={{ ...dotStyle(false), left: `${pct(valueMin)}%` }}
          onPointerDown={e => onPointerDown('min', e)}
        />
        {/* Max dot */}
        <div
          style={{ ...dotStyle(true), left: `${pct(valueMax)}%` }}
          onPointerDown={e => onPointerDown('max', e)}
        />
      </div>
      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: isDark ? '#bbb' : '#666' }}>
        <span>{getCurrencySymbol()}{valueMin}</span>
        <span>{getCurrencySymbol()}{valueMax}</span>
      </div>
    </div>
  );
};

const CollapsibleSection = ({ label, children, hasActive }) => {
  const [expanded, setExpanded] = useState(false);
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <div style={{ borderBottom: isDark ? '1px solid #333' : '1px solid #f0f0f0' }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '8px 0', fontSize: '12px', fontWeight: '600',
          color: hasActive ? '#fa4848' : (isDark ? '#bbb' : '#555'),
          textTransform: 'uppercase', letterSpacing: '0.4px',
        }}
      >
        <span>{label}{hasActive ? ' ●' : ''}</span>
        <FaChevronDown size={10} style={{ color: isDark ? '#777' : '#aaa', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
      </button>
      {expanded && (
        <div style={{ paddingBottom: '10px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const ChipGroup = ({ items, selected, onToggle }) => {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
      {items.map(item => {
        const active = selected.includes(item);
        return (
          <button key={item} onClick={() => onToggle(item)} style={{
            fontSize: '12px', padding: '3px 9px', borderRadius: '12px', cursor: 'pointer', border: 'none',
            background: active ? '#fa4848' : (isDark ? '#3a3a3a' : '#f0f0f0'),
            color: active ? '#fff' : (isDark ? '#ccc' : '#444'),
            fontWeight: active ? '600' : '400',
            transition: 'background 0.15s',
          }}>{item}</button>
        );
      })}
    </div>
  );
};

export default HomeFilter;
